import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { DateRange } from '@/types'
import {
  ComparisonFilters,
  KPIComparisonData,
  TrendComparisonData,
  CorrelationData,
  ComparisonViewMode,
  ComparisonSortOptions,
  DEFAULT_SELECTED_KPIS,
  MOCK_COMPARISON_DATA
} from '@/types/comparison'
import { get as apiGet } from '@/services/api'
import { ENDPOINTS } from '@/services/endpoints'
import { getUseMockData } from '@/contexts/SettingsContext'

interface ComparisonState {
  // Selection
  selectedAssetIds: string[]
  maxAssets: number

  // Filters
  filters: ComparisonFilters

  // Data
  kpiComparison: KPIComparisonData | null
  trendComparison: TrendComparisonData | null
  correlationData: CorrelationData | null

  // View State
  comparisonMode: ComparisonViewMode
  sortOptions: ComparisonSortOptions

  // Loading/Error
  loading: boolean
  error: string | null
  usingMockData: boolean

  // Actions - Selection
  toggleAsset: (assetId: string) => void
  setSelectedAssets: (assetIds: string[]) => void
  clearSelection: () => void

  // Actions - Filters
  setFilters: (filters: Partial<ComparisonFilters>) => void
  resetFilters: () => void

  // Actions - View
  setComparisonMode: (mode: ComparisonViewMode) => void
  setSortOptions: (options: ComparisonSortOptions) => void

  // Actions - Data Fetching
  fetchKPIComparison: () => Promise<void>
  fetchTrendComparison: (days?: number) => Promise<void>
  fetchCorrelations: () => Promise<void>
}

const defaultDateRange: DateRange = {
  start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
  end: new Date().toISOString(),
  label: 'Last 7 days'
}

const defaultFilters: ComparisonFilters = {
  dateRange: defaultDateRange,
  kpis: DEFAULT_SELECTED_KPIS,
  sensors: [],
  categories: []
}

export const useComparisonStore = create<ComparisonState>()(
  devtools(
    (set, get) => ({
      // Initial State
      selectedAssetIds: [],
      maxAssets: 5,
      filters: defaultFilters,
      kpiComparison: null,
      trendComparison: null,
      correlationData: null,
      comparisonMode: 'table',
      sortOptions: { field: 'reliability', direction: 'desc' },
      loading: false,
      error: null,
      usingMockData: false,

      // Selection Actions
      toggleAsset: (assetId: string) => {
        const { selectedAssetIds, maxAssets } = get()
        if (selectedAssetIds.includes(assetId)) {
          set({ selectedAssetIds: selectedAssetIds.filter(id => id !== assetId) })
        } else if (selectedAssetIds.length < maxAssets) {
          set({ selectedAssetIds: [...selectedAssetIds, assetId] })
        }
      },

      setSelectedAssets: (assetIds: string[]) => {
        const { maxAssets } = get()
        set({ selectedAssetIds: assetIds.slice(0, maxAssets) })
      },

      clearSelection: () => {
        set({ selectedAssetIds: [], kpiComparison: null, trendComparison: null })
      },

      // Filter Actions
      setFilters: (filters: Partial<ComparisonFilters>) => {
        set((state) => ({
          filters: { ...state.filters, ...filters }
        }))
      },

      resetFilters: () => {
        set({ filters: defaultFilters })
      },

      // View Actions
      setComparisonMode: (mode: ComparisonViewMode) => {
        set({ comparisonMode: mode })
      },

      setSortOptions: (options: ComparisonSortOptions) => {
        set({ sortOptions: options })
      },

      // Data Fetching Actions
      fetchKPIComparison: async () => {
        const { selectedAssetIds } = get()
        if (selectedAssetIds.length === 0) {
          set({ kpiComparison: null })
          return
        }

        set({ loading: true, error: null })

        // Check if mock data mode is enabled
        if (getUseMockData()) {
          console.log('Mock data mode enabled, using mock KPI comparison')
          const filteredMockData: KPIComparisonData = {
            ...MOCK_COMPARISON_DATA,
            assets: MOCK_COMPARISON_DATA.assets.filter(
              asset => selectedAssetIds.includes(asset.asset_id)
            )
          }
          set({
            kpiComparison: filteredMockData,
            loading: false,
            usingMockData: true,
            error: null
          })
          return
        }

        try {
          const assetIdsParam = selectedAssetIds.join(',')
          const data = await apiGet<KPIComparisonData>(
            `${ENDPOINTS.COMPARE_KPIS}?asset_ids=${assetIdsParam}`
          )
          set({ kpiComparison: data, loading: false, usingMockData: false })
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to fetch KPI comparison'
          console.error('KPI comparison API failed:', error)
          // Do NOT fall back to mock data when API is live - show error state instead
          set({
            kpiComparison: null,
            loading: false,
            usingMockData: false,
            error: errorMessage
          })
        }
      },

      fetchTrendComparison: async (days = 7) => {
        const { selectedAssetIds } = get()
        if (selectedAssetIds.length === 0) {
          set({ trendComparison: null })
          return
        }

        set({ loading: true, error: null })

        // Helper to generate mock trend data
        const generateMockTrends = () => {
          const timestamps = Array.from({ length: days }, (_, i) => {
            const date = new Date()
            date.setDate(date.getDate() - (days - 1 - i))
            return date.toISOString().split('T')[0]
          })

          return {
            timestamps,
            trends: selectedAssetIds.reduce((acc, assetId) => {
              const baseReliability = 80 + Math.random() * 15
              acc[assetId] = {
                reliability: timestamps.map((_, i) => baseReliability + Math.random() * 5 + i * 0.2),
                anomaly_score: timestamps.map(() => Math.random() * 0.5),
                health_score: timestamps.map((_, i) => 70 + Math.random() * 20 + i * 0.3)
              }
              return acc
            }, {} as TrendComparisonData['trends']),
            timestamp: new Date().toISOString()
          }
        }

        // Check if mock data mode is enabled
        if (getUseMockData()) {
          console.log('Mock data mode enabled, using mock trend comparison')
          set({ trendComparison: generateMockTrends(), loading: false, usingMockData: true })
          return
        }

        try {
          const assetIdsParam = selectedAssetIds.join(',')
          const data = await apiGet<TrendComparisonData>(
            `${ENDPOINTS.COMPARE_KPIS_TRENDS}?asset_ids=${assetIdsParam}&days=${days}`
          )
          set({ trendComparison: data, loading: false, usingMockData: false })
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to fetch trend comparison'
          console.error('Trend API failed:', error)
          // Do NOT fall back to mock data when API is live - show error state instead
          set({ trendComparison: null, loading: false, usingMockData: false, error: errorMessage })
        }
      },

      fetchCorrelations: async () => {
        const { selectedAssetIds } = get()
        if (selectedAssetIds.length === 0) {
          set({ correlationData: null })
          return
        }

        set({ loading: true, error: null })

        // Helper to generate mock correlation data
        const generateMockCorrelation = () => {
          const kpis = ['reliability', 'availability', 'mtbf', 'mttr', 'anomaly_rate']
          const n = kpis.length
          const matrix: number[][] = []
          for (let i = 0; i < n; i++) {
            matrix[i] = []
            for (let j = 0; j < n; j++) {
              if (i === j) {
                matrix[i][j] = 1.0
              } else if (j < i) {
                matrix[i][j] = matrix[j][i]
              } else {
                matrix[i][j] = Math.round((Math.random() * 2 - 1) * 100) / 100
              }
            }
          }
          return {
            kpis,
            matrix,
            assets: selectedAssetIds,
            timestamp: new Date().toISOString()
          }
        }

        // Check if mock data mode is enabled
        if (getUseMockData()) {
          console.log('Mock data mode enabled, using mock correlation data')
          set({
            correlationData: generateMockCorrelation(),
            loading: false,
            usingMockData: true
          })
          return
        }

        try {
          const assetIdsParam = selectedAssetIds.join(',')
          const data = await apiGet<CorrelationData>(
            `${ENDPOINTS.COMPARE_KPIS_CORRELATIONS}?asset_ids=${assetIdsParam}`
          )
          set({ correlationData: data, loading: false, usingMockData: false })
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to fetch correlations'
          console.error('Correlation API failed:', error)
          // Do NOT fall back to mock data when API is live - show error state instead
          set({
            correlationData: null,
            loading: false,
            usingMockData: false,
            error: errorMessage
          })
        }
      }
    }),
    { name: 'comparison-store' }
  )
)
